import { useMemo, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@repo/tokens";
import type { QuestionData } from "../../models/GetQuestions";
import { QUESTION_TYPES } from "../../types/questionTypes";
import { getNumericQuestionStyles } from "./numericQuestionDynamicStyles";
import styles from "./NumericQuestionTypeScreen.module.scss";

const getCorrectAnswerStr = (correct_answer: (number | null)[][]): string => {
  const first = correct_answer?.[0];
  const val = Array.isArray(first) ? first[0] : first;
  return val != null ? String(val) : "";
};
type Props = {
  questionData: QuestionData;
  selectedValue: string;
  setSelectedValue: (val: string) => void;
  onDone?: () => void;
};

export const NumericQuestionTypeScreen = ({
  questionData,
  selectedValue,
  setSelectedValue,
}: Props) => {
  const { t } = useTranslation("selflearn");
  const { theme } = useTheme();
  const tokens = useMemo(() => getNumericQuestionStyles(theme), [theme]);
  const kn = tokens.keyNeutral;
  const kp = tokens.keyPressed;
  const ks = tokens.keySelected;

  const isNumeric = questionData.question_type === QUESTION_TYPES.NUMERIC;
  const isAttempted = questionData.isAttempted ?? false;
  const isCorrect = questionData.isCorrectAnswer;
  const correctStr = getCorrectAnswerStr(
    questionData.question?.correct_answer ?? [],
  );

  const handleKey = (key: string) => {
    if (isAttempted) return;
    if (key === "clear") {
      setSelectedValue("");
      return;
    }
    if (key === ".") {
      if (!isNumeric || selectedValue.includes(".")) return;
      setSelectedValue(selectedValue + ".");
      return;
    }
    if (key === "-") {
      if (selectedValue !== "") return;
      setSelectedValue("-");
      return;
    }
    setSelectedValue(selectedValue + key);
  };

  const inputStyle = isAttempted
    ? isCorrect
      ? tokens.correct
      : tokens.wrong
    : tokens.neutral;

  const numpadKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

  const rootStyle = {
    "--numeric-key-bg": kn.backgroundColor,
    "--numeric-key-border": kn.borderColor,
    "--numeric-key-text": kn.textColor,
    "--numeric-key-border-w": `${kn.borderWidth}px`,
    "--numeric-key-radius": `${kn.borderRadius}px`,
    "--numeric-key-active-bg": kp.backgroundColor,
    "--numeric-key-active-text": kp.textColor,
    "--numeric-key-selected-bg": ks.backgroundColor,
    "--numeric-key-selected-text": ks.textColor,
  } as CSSProperties;

  return (
    <div className={styles.root} style={rootStyle}>
      {isAttempted ? (
        <div className={styles.attemptedBlock}>
          <div
            className={styles.inputBox}
            style={{
              borderColor: inputStyle.borderColor,
              backgroundColor: inputStyle.backgroundColor,
            }}
          >
            <div
              className={styles.inputText}
              style={{ color: inputStyle.textColor }}
            >
              {selectedValue || "—"}
            </div>
          </div>
          {!isCorrect && (
            <div
              className={styles.inputBox}
              style={{
                borderColor: tokens.correct.borderColor,
                backgroundColor: tokens.correct.backgroundColor,
              }}
            >
              <div
                className={styles.inputText}
                style={{ color: tokens.correct.textColor }}
              >
                {correctStr}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`${styles.inputNeutral} ${selectedValue !== "" ? styles.inputNeutralHasValue : ""}`}
        >
          <span className={styles.inputNeutralText}>
            {selectedValue || " "}
          </span>
        </div>
      )}

      {!isAttempted && (
        <div className={styles.numpadSection}>
          <div className={styles.numpadRow}>
            {numpadKeys.slice(0, 3).map((k) => (
              <NumKey key={k} label={k} onPress={() => handleKey(k)} />
            ))}
          </div>
          <div className={styles.numpadRow}>
            {numpadKeys.slice(3, 6).map((k) => (
              <NumKey key={k} label={k} onPress={() => handleKey(k)} />
            ))}
          </div>
          <div className={styles.numpadRow}>
            {numpadKeys.slice(6, 9).map((k) => (
              <NumKey key={k} label={k} onPress={() => handleKey(k)} />
            ))}
          </div>
          <div className={styles.numpadRow}>
            {isNumeric ? (
              <NumKey label="." onPress={() => handleKey(".")} />
            ) : null}
            <NumKey label="0" onPress={() => handleKey("0")} />
            <NumKey label="-" onPress={() => handleKey("-")} />
          </div>
          <button
            type="button"
            className={`${styles.clearBtn} ${selectedValue === "" ? styles.clearBtnDisabled : styles.clearBtnEnabled}`}
            disabled={selectedValue === ""}
            onClick={() => handleKey("clear")}
          >
            {t("reviseQuestions.numeric.clear")}
          </button>
        </div>
      )}
    </div>
  );
};

function NumKey({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <button type="button" className={`${styles.numKey}`} onClick={onPress}>
      {label}
    </button>
  );
}
